import { ChangeDetectorRef, Component, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { DetailedModalComponent } from '../detailed-modal/detailed-modal.component';
import {
  HtmlInputType,
  InputLayout,
  ZiroTextComponent,
  ZiroTextConfig,
} from '../../forms/fields/text/ziro-text.component';
import { HtmlCheckboxType, ZiroCheckboxConfig } from '../../forms/fields/checkbox/ziro-checkbox.component';
import { ZiroFormConfig, ZiroFormsValidationState } from '../../forms/ziro-forms-models';
import { ModalBodyClass } from '../modal-body';
import { Observable, of, tap } from 'rxjs';
import { ZiroFormsModule } from '../../forms/ziro-forms.module';
import { ButtonSizes, ButtonStyles, ZiroButtonComponent } from '../../button/button.component';
import { ZiroIcons } from '../../shared/models/ziro-icons.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CucmE164TransformationRule } from '../../shared/models/generated/ziroModels';
import { MicrosoftCucmE164TransformationRulesContext } from '../../shared/contexts/microsoft-cucm-e164-transformation-rules.context';
import { MicrosoftCucmAutomaticMappingResource } from '../../shared/resources/microsoft-cucm-automatic-mapping.resource';
import { NgClass } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastTypes } from '../../shared/services/abstract/toast.service.abstract';
import { ToastService } from '../../shared/services/toast.service';
import { AboutCardService } from '../../shared/services/microsoft/about-card.service';
import { SafeHtml } from '@angular/platform-browser';
import { PipesModule } from '../../shared/pipes/pipes.module';
import { delayWhen } from 'rxjs/operators';
import { TelephoneNumberFilter } from '../../shared/filters/telephone-number.filter';
import { migrationsE164AndExtensionsRegex } from '../../shared/e164-extension-validators-regex';

const AUTOMATIC_MAPPING_FIELD_ID = 'automaticMappingEnabled';

@Component({
  selector: 'ziro-number-translation-rules-modal',
  standalone: true,
  imports: [ZiroFormsModule, ZiroButtonComponent, NgClass, TranslateModule, PipesModule],
  templateUrl: './number-translation-rules-modal.component.html',
  styleUrls: ['./number-translation-rules-modal.component.scss'],
})
export class NumberTranslationRulesModalComponent extends ModalBodyClass implements OnInit {
  private _activeModal = inject(NgbActiveModal);
  private _modalComponent = inject<DetailedModalComponent<any>>(DetailedModalComponent);
  private _translateService = inject(TranslateService);
  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _msCucmE164TransformationRulesContext = inject(MicrosoftCucmE164TransformationRulesContext);
  private _automaticMappingResource = inject(MicrosoftCucmAutomaticMappingResource);
  private _toastService = inject(ToastService);
  private _aboutCardService = inject(AboutCardService);
  private _telephoneNumberFilter = inject(TelephoneNumberFilter);

  @ViewChild('errorTemplate') errorTemplate: TemplateRef<any>;

  e164Rules: CucmE164TransformationRule[];
  directoryNumbers: string[];
  ziroIcons = ZiroIcons;
  buttonSize = ButtonSizes;
  buttonStyles = ButtonStyles;
  alertHtml: SafeHtml;

  fieldIds: {
    fromFieldId: `numberRangeToTransform-${string}`;
    toFieldId: `e164TransformationRule-${string}`;
    descriptionFieldId: `description-${string}`;
    matchCount?: number;
  }[] = [];

  automaticMappingFieldId = AUTOMATIC_MAPPING_FIELD_ID;

  private _initialAutomaticMappingEnabled = false;

  formConfig: ZiroFormConfig = {
    fields: {
      [AUTOMATIC_MAPPING_FIELD_ID]: {
        label: 'tkey;number_translation_rules.automatic_mapping.label',
        dataAutomation: 'number-translation-rules-automatic-mapping-toggle',
        componentConfig: new ZiroCheckboxConfig({ checkboxType: HtmlCheckboxType.SWITCH }),
      },
    },
    options: {
      columnClasses: {
        label: 'col-auto',
        input: 'col-auto',
      },
    },
  };

  ngOnInit(): void {
    this.e164Rules = [...this._modalComponent.modalViewProperties.e164TransformationRules];
    this.directoryNumbers = [...this._modalComponent.modalViewProperties.directoryNumbers];
    this._msCucmE164TransformationRulesContext.setBaseUrl(
      this._modalComponent.modalViewProperties.tenantId,
      this._modalComponent.modalViewProperties.reportId,
    );
    this._initialAutomaticMappingEnabled = this._modalComponent.modalViewProperties.automaticMappingEnabled;
    this.entity = { [AUTOMATIC_MAPPING_FIELD_ID]: this._initialAutomaticMappingEnabled };
    if (!this.e164Rules.length) {
      this.addRow();
    } else {
      this._initFormData();
    }

    this._aboutCardService.getAboutCard(95).subscribe((html) => {
      if (html) {
        this.alertHtml = html;
      }
    });
  }

  addRow(): void {
    this.displayError = false;
    this.isFormSubmitted = false;
    const newFieldUuid = crypto.randomUUID();
    const fromId: `numberRangeToTransform-${string}` = `numberRangeToTransform-${newFieldUuid}`;
    const toId: `e164TransformationRule-${string}` = `e164TransformationRule-${newFieldUuid}`;
    const descId: `description-${string}` = `description-${newFieldUuid}`;

    this.fieldIds.push({ fromFieldId: fromId, toFieldId: toId, descriptionFieldId: descId });

    this._setNewFormFields(fromId, toId, descId);

    this.entity = {
      ...this.entity,
      [fromId]: '',
      [toId]: '',
      [descId]: '',
    };
    this.entitySource.next(this.entity);
    this._changeDetectorRef.detectChanges();
  }

  deleteRow(index: number): void {
    const removed = this.fieldIds.splice(index, 1)[0];
    delete this.formConfig.fields[removed.fromFieldId];
    delete this.formConfig.fields[removed.toFieldId];
    delete this.formConfig.fields[removed.descriptionFieldId];

    if (index === 0) {
      Object.values(this.fieldIds[0]).forEach((fieldId) => {
        if (typeof fieldId !== 'string') {
          return;
        }
        const labelType: string = (fieldId as string)
          .split('-')[0]
          .replace('numberRangeToTransform', 'from')
          .replace('e164TransformationRule', 'to');

        this.formConfig.fields[fieldId].label = `tkey;number_translation_rules.${labelType}.label`;
        this.formConfig.fields[fieldId].subLabel = `tkey;number_translation_rules.${labelType}.sub_label`;
      });
    }

    const updated = { ...this.entity };
    delete updated[removed.fromFieldId];
    delete updated[removed.toFieldId];
    delete updated[removed.descriptionFieldId];
    this.entity = updated;
    this.entitySource.next(this.entity);

    this._changeDetectorRef.detectChanges();
    this.validateAllFields();
  }

  onInvalidSubmit(): Observable<CucmE164TransformationRule[] | void> {
    if (this.fieldIds.length === 1 && !this.isFirstRowPopulated()) {
      const payload: CucmE164TransformationRule[] = [];
      this.displayError = false;
      this._activeModal.close();
      return this._msCucmE164TransformationRulesContext.put(payload).pipe(
        delayWhen(() => this._saveAutomaticMapping()),
        delayWhen(() => this._modalComponent.submitFn()),
        tap(() => this._toastMessage()),
      );
    } else {
      this.displayError = true;
      return of(null);
    }
  }

  isFirstRowPopulated(): boolean {
    const { fromFieldId, toFieldId, descriptionFieldId } = this.fieldIds[0];
    return [fromFieldId, toFieldId, descriptionFieldId].some((fieldId) => this.entity[fieldId] !== '');
  }

  clearForm(): void {
    this.displayError = false;

    this.entitySource.next({
      [this.fieldIds[0].fromFieldId]: '',
      [this.fieldIds[0].toFieldId]: '',
      [this.fieldIds[0].descriptionFieldId]: '',
      [AUTOMATIC_MAPPING_FIELD_ID]: this.entity[AUTOMATIC_MAPPING_FIELD_ID],
    });

    this.setIsExisting(false);
    this._ziroFormStateService.setIsFormDirty(false);
    this.isFormSubmitted = false;
    this.fieldComponents.forEach((item: ZiroTextComponent) => {
      item.isDirty = false;
      item.showValidation = false;
      item.isFormSubmitted = this.isFormSubmitted;
      item.fieldData = '';
      item.showAsyncValidation = false;
    });
  }

  submit(): Observable<CucmE164TransformationRule[]> {
    const payload: CucmE164TransformationRule[] = this.fieldIds.map(
      ({ fromFieldId, toFieldId, descriptionFieldId }) => ({
        numberRangeToTransform: this.entity[fromFieldId].toUpperCase(),
        e164TransformationRule: this.entity[toFieldId].toUpperCase(),
        description: this.entity[descriptionFieldId],
      }),
    );

    return this._msCucmE164TransformationRulesContext.put(payload).pipe(
      delayWhen(() => this._saveAutomaticMapping()),
      delayWhen(() => this._modalComponent.submitFn()),
      tap(() => this._toastMessage()),
    );
  }

  private _initFormData(): void {
    this.e164Rules.forEach((rule) => {
      const uuid = crypto.randomUUID();
      const fromId = `numberRangeToTransform-${uuid}` as const;
      const toId = `e164TransformationRule-${uuid}` as const;
      const descId = `description-${uuid}` as const;

      this.fieldIds.push({ fromFieldId: fromId, toFieldId: toId, descriptionFieldId: descId });

      this._setNewFormFields(fromId, toId, descId);

      this.entity[fromId] = rule.numberRangeToTransform;
      this.entity[toId] = rule.e164TransformationRule;
      this.entity[descId] = rule.description;
    });

    this.entitySource.next(this.entity);
    this._changeDetectorRef.detectChanges();
  }

  private _setNewFormFields(fromId: string, toId: string, descId: string): void {
    const showLabels = this.fieldIds.length === 1;

    /* From Field */
    this.formConfig.fields[fromId] = {
      label: showLabels ? 'tkey;number_translation_rules.from.label' : '',
      subLabel: showLabels ? 'tkey;number_translation_rules.from.sub_label' : '',
      dataAutomation: `from-cucm-directory-number-${this.fieldIds.length - 1}`,
      required: true,
      componentConfig: new ZiroTextConfig({
        inputLayout: InputLayout.COL,
        htmlInputType: HtmlInputType.TEXT,
        placeholder: 'tkey;number_translation_rules.from.placeholder',
        toUppercase: true,
      }),
      validation: [
        {
          validator: (val: string) =>
            /^\+?\d*[Xx]+$/.test(val) ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID,
          message: 'tkey;number_translation_rules.from.validation.message.invalid_format',
        },
        {
          validator: (val: string) => {
            const matchCount = this.fieldIds.filter((rule) => {
              return this.entity[rule.fromFieldId].toLowerCase() === val.toLowerCase() && rule.fromFieldId !== fromId;
            }).length;
            return matchCount >= 1 ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID;
          },
          message: () => this._getFromFieldDuplicateValueMsg(fromId),
        },
        {
          validator: (val: string) => this._countMatchingDns(val, fromId),
          successMessage: () => this._getFromFieldSuccessMsg(fromId),
        },
      ],
    };

    /* To Field */
    this.formConfig.fields[toId] = {
      label: showLabels ? 'tkey;number_translation_rules.to.label' : null,
      subLabel: showLabels ? 'tkey;number_translation_rules.to.sub_label' : null,
      dataAutomation: `to-msft-teams-number-${this.fieldIds.length - 1}`,
      required: true,
      componentConfig: new ZiroTextConfig({
        inputLayout: InputLayout.COL,
        htmlInputType: HtmlInputType.TEXT,
        placeholder: 'tkey;number_translation_rules.to.placeholder',
        toUppercase: true,
      }),
      validation: [
        {
          validator: (val: string) =>
            /^\+?[0-9]+([Xx]+)$/.test(val) ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID,
          message: 'tkey;number_translation_rules.to.validation.message.invalid_format',
        },
        {
          validator: (toVal: string, fromVal: string) => {
            const xCount = (string: string): number => string.match(/[Xx]/g)?.length ?? 0;
            return xCount(toVal) <= fromVal.length ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID;
          },
          message: 'tkey;number_translation_rules.to.validation.message.exceeded_x_count',
          injectValuesFromFields: [fromId],
        },
        {
          validator: (toVal: string, fromVal: string) => {
            const xCount = (string: string): number => string.match(/[Xx]/g)?.length ?? 0;
            return xCount(fromVal) > xCount(toVal) ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID;
          },
          message: 'tkey;number_translation_rules.to.validation.message.insufficient_x_count',
          injectValuesFromFields: [fromId],
        },
        {
          validator: (val: string) => {
            return this._isValidExtensionOrNumber(val)
              ? ZiroFormsValidationState.VALID
              : ZiroFormsValidationState.INVALID;
          },
          message: 'tkey;number_translation_rules.to.validation.message.invalid_number_format',
        },
      ],
    };

    /* Description Field */
    this.formConfig.fields[descId] = {
      label: showLabels ? 'tkey;number_translation_rules.description.label' : null,
      subLabel: showLabels ? 'tkey;number_translation_rules.description.sub_label' : null,
      dataAutomation: `e164-translation-description-${this.fieldIds.length - 1}`,
      required: true,
      componentConfig: new ZiroTextConfig({
        inputLayout: InputLayout.COL,
        htmlInputType: HtmlInputType.TEXT,
        placeholder: 'tkey;number_translation_rules.description.placeholder',
      }),
    };
  }

  private _countMatchingDns(val: string, fromId: string): Observable<ZiroFormsValidationState> {
    const field = this.fieldIds.find((rule) => rule.fromFieldId === fromId);
    if (!field) {
      return;
    }
    try {
      const escaped = val.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const pattern = '^' + escaped.replace(/x/gi, '\\d') + '$';
      const regex = new RegExp(pattern);
      const matches = this.directoryNumbers.filter((dn) => regex.test(dn));

      const field = this.fieldIds.find((rule) => rule.fromFieldId === fromId);
      field.matchCount = matches.length;
    } catch {
      field.matchCount = 0;
    }

    return of(ZiroFormsValidationState.VALID);
  }

  private _getFromFieldDuplicateValueMsg(fromId: string): string {
    const field = this.fieldComponents.find((field) => field.fieldId() === fromId);
    const duplicateValues = this.fieldIds
      .map((rule) => rule.fromFieldId)
      .filter((fieldId) => fieldId !== fromId && this.entity[fieldId].toLowerCase() === field.fieldData.toLowerCase());
    if (duplicateValues.length) {
      const duplicateRuleName = this.entity[duplicateValues[0].replace('numberRangeToTransform', 'description')];
      if (duplicateRuleName) {
        return this._translateService.instant('tkey;number_translation_rules.from.validation.message.duplicate_value', {
          ruleName: duplicateRuleName,
        });
      } else {
        return this._translateService.instant(
          'tkey;number_translation_rules.from.validation.message.duplicate_value.no_description',
        );
      }
    } else {
      return '';
    }
  }

  private _getFromFieldSuccessMsg(fromId: string): string {
    const matchCount = this.fieldIds.find((rule) => rule.fromFieldId === fromId)?.matchCount ?? 0;

    return matchCount !== 1
      ? this._translateService.instant('tkey;number_translation_rules.from.validation.message.success', {
          count: matchCount,
        })
      : this._translateService.instant('tkey;number_translation_rules.from.validation.message.success.singular', {
          count: matchCount,
        });
  }

  private _toastMessage(): void {
    this._toastService.push(
      ToastTypes.SUCCESS,
      ZiroIcons.SAVE_SUCCESS,
      'tkey;shared.toast.save.success.title',
      'tkey;number_translation_rules.toast.message',
    );
  }

  private _isValidExtensionOrNumber(val: string): boolean {
    const valAsNumber = val.replace(/[Xx]/g, '0');
    return migrationsE164AndExtensionsRegex.test(valAsNumber);
  }

  private _saveAutomaticMapping(): Observable<void> {
    if (!!this.entity[AUTOMATIC_MAPPING_FIELD_ID] === this._initialAutomaticMappingEnabled) {
      return of(undefined);
    }
    return this._automaticMappingResource.put(
      this._modalComponent.modalViewProperties.tenantId,
      this._modalComponent.modalViewProperties.reportId,
      { enabled: !!this.entity[AUTOMATIC_MAPPING_FIELD_ID] },
    );
  }
}
